import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import {
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ViewColumnsIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  BriefcaseIcon,
  BookOpenIcon,
  ArchiveBoxIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import BrandLogo from '../brand/BrandLogo';

const navItems = [
  { to: '/admin/dashboard', icon: HomeIcon, label: 'Dashboard', testId: 'admin-nav-dashboard' },
  { to: '/admin/pipeline', icon: ViewColumnsIcon, label: 'Pipeline', testId: 'admin-nav-pipeline' },
  { to: '/admin/candidates', icon: UsersIcon, label: 'Candidates', testId: 'admin-nav-candidates' },
  { to: '/admin/talent-pool', icon: ArchiveBoxIcon, label: 'Talent Pool', testId: 'admin-nav-talent-pool' },
  { to: '/admin/assessments', icon: ClipboardDocumentCheckIcon, label: 'Assessments', testId: 'admin-nav-assessments' },
  { to: '/admin/email-templates', icon: EnvelopeIcon, label: 'Email Templates', hrmOnly: true, testId: 'admin-nav-email-templates' },
  { to: '/admin/career-settings', icon: BriefcaseIcon, label: 'Career Page', hrmOnly: true, testId: 'admin-nav-career-settings' },
  { to: '/admin/settings', icon: Cog6ToothIcon, label: 'Settings', testId: 'admin-nav-settings' },
  { to: '/admin/roles', icon: ShieldCheckIcon, label: 'Roles & Permissions', adminOnly: true, testId: 'admin-nav-roles' },
  { to: '/admin/users', icon: UserGroupIcon, label: 'Users', adminOnly: true, testId: 'admin-nav-users' },
  { to: '/admin/help', icon: BookOpenIcon, label: 'Help / Manuals', testId: 'admin-nav-help' },
];

export default function Sidebar({ open = false, onClose }) {
  const { user, logout, hasRole } = useAuth();

  return (
    <aside
      id="admin-sidebar"
      // h-dvh (not h-screen) so the Sign Out button clears the mobile URL bar.
      className={clsx(
        'fixed left-0 top-0 z-drawer h-dvh w-72 bg-gradient-to-b from-brand-700 to-brand-900 text-white flex flex-col border-r border-white/10',
        // visibility is part of the transition so the drawer slides out instead of snapping.
        'transition-[transform,visibility] duration-200 lg:visible lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full invisible'
      )}
      data-testid="admin-sidebar"
    >
      <div className="px-4 lg:px-5 py-4 lg:py-5 border-b border-white/10">
        <div className="flex items-center gap-2.5 lg:gap-3 rounded-2xl bg-white/10 border border-white/15 p-3 lg:p-3.5">
          <div className="h-10 w-10 lg:h-12 lg:w-12 rounded-xl bg-slate-950/50 border border-white/15 flex items-center justify-center">
            <BrandLogo variant="darkBg" showWordmark={false} size={24} className="opacity-95 lg:w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] lg:text-sm font-semibold leading-tight">Genesis Creations</p>
            <p className="text-[11px] lg:text-xs text-brand-200 mt-0.5 tracking-wide">Talent Hub</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation menu"
            className="lg:hidden p-1.5 -mr-1 text-brand-100 hover:text-white hover:bg-white/10 rounded-lg focus-ring transition-colors duration-150"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <nav aria-label="Main" className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto" data-testid="admin-nav">
        {navItems.map(item => {
          if (item.adminOnly && !hasRole('admin')) return null;
          if (item.hrmOnly && !hasRole('admin') && !hasRole('hrm')) return null;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
                  isActive
                    ? 'bg-white/18 text-white shadow-sm'
                    : 'text-brand-100/90 hover:bg-white/10 hover:text-white'
                )
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-white/10 bg-black/10">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5">
          <div className="h-9 w-9 rounded-full bg-brand-400 text-brand-900 flex items-center justify-center text-xs font-bold">
            {user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-brand-200 truncate capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 mt-2 w-full text-brand-100 hover:text-white hover:bg-white/10 rounded-xl text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
